/**
 * CASEWORK STUDIO stage B (2026-10-09, Matt: "include built-in appliances like refrigerator
 * columns, microwaves, ice makers, beverage fridges … research what else we might want in a
 * luxury home and provide it dimensionally accurate"). The catalog of built-in appliances a
 * cabinet box can HOLD. Decision (asked): an appliance is a REAL appliance element (object kind
 * `builtInAppliance`, its true size) sitting in an opening the cabinetry builds around it;
 * panel-ready units wear the cabinet's own fronts (profile + hardware), stainless and glass units
 * draw their own. Sizes from manufacturer install / design guides — the research, with sources and
 * what stayed "typ.", is docs/bim-plan/31-casework-appliances.md. Names are characters, never brands.
 *
 * MOUNTS — where the opening is in its box:
 *   undercounter  the whole base box below the counter (34-1/2" — the undercounter standard)
 *   baseTop       the top of a base box, under the counter (a microwave or warming drawer), the
 *                 rest of the box drawers
 *   column        a tall box: the appliance from the floor to 84", a cabinet over it
 *   tall          a tall box (an oven tower): drawers below the stack, the stack from its sill,
 *                 doors over it
 */

export type ApplianceMount = 'undercounter' | 'baseTop' | 'column' | 'tall'
export type ApplianceFront = 'panel' | 'stainless' | 'glass'

/** one appliance in an opening (an entry may stack several, bottom to top) */
export interface ApplianceUnit {
  key: string
  label: string
  /** the 3D look (bim-geometry builtInAppliance) */
  look: string
  /** front height, inches (the opening it needs) */
  hIn: number
  front: ApplianceFront
  /** panel-ready units: the cabinet fronts it wears */
  panelLook?: 'door' | 'drawers2' | 'drawer'
}

export interface ApplianceEntry {
  value: string
  label: string
  group: 'Refrigeration' | 'Cooking' | 'Beverage' | 'Cleaning'
  mount: ApplianceMount
  /** nominal widths, inches */
  widths: number[]
  /** bottom to top */
  units: ApplianceUnit[]
  /** a tall stack's opening bottom above the floor, inches */
  sillIn?: number
  /** the box's toe kick runs in front ('cabinet') or the unit has its own grille / sits on the floor ('none') */
  toe: 'cabinet' | 'none'
  note: string
}

const U = {
  panelDoor: (hIn: number): ApplianceUnit => ({ key: 'panelDoor', label: 'Panel-ready', look: 'panel', hIn, front: 'panel', panelLook: 'door' }),
  panelDrawers: (hIn: number): ApplianceUnit => ({ key: 'panelDrawers', label: 'Panel-ready drawers', look: 'panel', hIn, front: 'panel', panelLook: 'drawers2' }),
  oven: { key: 'oven', label: 'Wall oven', look: 'oven', hIn: 28.5, front: 'stainless' } as ApplianceUnit,
  ovenDouble: { key: 'ovenDouble', label: 'Double wall oven', look: 'ovenDouble', hIn: 50.875, front: 'stainless' } as ApplianceUnit,
  speedOven: { key: 'speedOven', label: 'Speed oven', look: 'ovenSmall', hIn: 17.875, front: 'stainless' } as ApplianceUnit,
  steamOven: { key: 'steamOven', label: 'Combi-steam oven', look: 'ovenSmall', hIn: 17.875, front: 'stainless' } as ApplianceUnit,
  microwave: { key: 'microwave', label: 'Built-in microwave', look: 'microwave', hIn: 20, front: 'stainless' } as ApplianceUnit,
  microwaveDrawer: { key: 'microwaveDrawer', label: 'Microwave drawer', look: 'microwaveDrawer', hIn: 15.125, front: 'stainless' } as ApplianceUnit,
  warmingDrawer: { key: 'warmingDrawer', label: 'Warming drawer', look: 'drawer', hIn: 10.375, front: 'stainless' } as ApplianceUnit,
  cupWarmer: { key: 'cupWarmer', label: 'Cup warming drawer', look: 'drawer', hIn: 5.5, front: 'stainless' } as ApplianceUnit,
  vacuumDrawer: { key: 'vacuumDrawer', label: 'Vacuum seal drawer', look: 'drawer', hIn: 5.5, front: 'stainless' } as ApplianceUnit,
  coffee: { key: 'coffee', label: 'Coffee system', look: 'coffee', hIn: 17.875, front: 'stainless' } as ApplianceUnit,
  wine: (hIn: number): ApplianceUnit => ({ key: 'wine', label: 'Wine / beverage', look: 'wine', hIn, front: 'glass' }),
  fridgeOU: { key: 'fridgeOU', label: 'Built-in refrigerator, over-and-under', look: 'fridgeOU', hIn: 84, front: 'stainless' } as ApplianceUnit,
  fridgeFD: { key: 'fridgeFD', label: 'Built-in refrigerator, French door', look: 'fridgeFD', hIn: 84, front: 'stainless' } as ApplianceUnit,
  fridgeSBS: { key: 'fridgeSBS', label: 'Built-in refrigerator, side-by-side', look: 'fridgeSBS', hIn: 84, front: 'stainless' } as ApplianceUnit,
}

/** the catalog, by group (inches) */
export const CASEWORK_APPLIANCES: ApplianceEntry[] = [
  // ---- Refrigeration ----
  { value: 'columnFridge', label: 'Column refrigerator', group: 'Refrigeration', mount: 'column', widths: [18, 24, 30, 36], units: [U.panelDoor(84)], toe: 'cabinet', note: 'Integrated, panel-ready, 84" tall, 24" deep; the cabinet toe kick runs in front' },
  { value: 'columnFreezer', label: 'Column freezer', group: 'Refrigeration', mount: 'column', widths: [18, 24, 30, 36], units: [U.panelDoor(84)], toe: 'cabinet', note: 'Integrated, panel-ready, 84" tall; pair it with a column refrigerator' },
  { value: 'wineColumn', label: 'Wine column', group: 'Refrigeration', mount: 'column', widths: [18, 24, 30], units: [U.wine(84)], toe: 'cabinet', note: 'Glass door in a panel frame, 84" tall' },
  { value: 'fridgeOU', label: 'Built-in, over-and-under', group: 'Refrigeration', mount: 'column', widths: [30, 36], units: [U.fridgeOU], toe: 'none', note: 'Stainless, 84" tall, stands on the floor with its grille at the top (no toe kick)' },
  { value: 'fridgeFD', label: 'Built-in, French door', group: 'Refrigeration', mount: 'column', widths: [36, 42], units: [U.fridgeFD], toe: 'none', note: 'Stainless, 84" tall, grille at the top' },
  { value: 'fridgeSBS', label: 'Built-in, side-by-side', group: 'Refrigeration', mount: 'column', widths: [36, 42, 48], units: [U.fridgeSBS], toe: 'none', note: 'Stainless, 84" tall, grille at the top' },
  { value: 'fridgeDrawers', label: 'Refrigerator drawers', group: 'Refrigeration', mount: 'undercounter', widths: [24, 27, 30, 36], units: [U.panelDrawers(34.5)], toe: 'none', note: 'Panel-ready refrigerator / freezer drawers, 34-1/2" under the counter; its own grille' },
  { value: 'ucFridge', label: 'Undercounter refrigerator', group: 'Refrigeration', mount: 'undercounter', widths: [24], units: [U.panelDoor(34.5)], toe: 'none', note: 'Panel-ready, 34-1/2"; its own grille' },
  // ---- Beverage ----
  { value: 'wineUnder', label: 'Undercounter wine', group: 'Beverage', mount: 'undercounter', widths: [15, 24], units: [U.wine(34.5)], toe: 'none', note: 'Glass door, 34-1/2"; its own grille' },
  { value: 'beverage', label: 'Beverage center', group: 'Beverage', mount: 'undercounter', widths: [15, 24], units: [U.wine(34.5)], toe: 'none', note: 'Glass door, 34-1/2"; its own grille' },
  { value: 'iceMaker', label: 'Clear-ice maker', group: 'Beverage', mount: 'undercounter', widths: [15], units: [U.panelDoor(34.5)], toe: 'none', note: 'Panel-ready, 15" wide, 34-1/2"; needs a drain' },
  { value: 'coffee', label: 'Coffee system + cup warmer', group: 'Beverage', mount: 'tall', widths: [24], units: [U.cupWarmer, U.coffee], sillIn: 42, toe: 'cabinet', note: 'Plumbed espresso system over a cup warming drawer, in a tall cabinet' },
  // ---- Cooking ----
  { value: 'wallOven', label: 'Wall oven', group: 'Cooking', mount: 'tall', widths: [30], units: [U.oven], sillIn: 32, toe: 'cabinet', note: 'Single 30" oven, 28-1/2" tall, in a tall cabinet' },
  { value: 'wallOvenDouble', label: 'Double wall oven', group: 'Cooking', mount: 'tall', widths: [30], units: [U.ovenDouble], sillIn: 17, toe: 'cabinet', note: '50-7/8" tall; the guide’s 17" cutout bottom' },
  { value: 'ovenSpeed', label: 'Wall oven + speed oven', group: 'Cooking', mount: 'tall', widths: [30], units: [U.oven, U.speedOven], sillIn: 20, toe: 'cabinet', note: 'Speed oven at eye level over a single oven' },
  { value: 'ovenSteam', label: 'Wall oven + steam oven', group: 'Cooking', mount: 'tall', widths: [30], units: [U.oven, U.steamOven], sillIn: 20, toe: 'cabinet', note: 'Combi-steam oven over a single oven' },
  { value: 'ovenWarmer', label: 'Wall oven + warming drawer', group: 'Cooking', mount: 'tall', widths: [30], units: [U.warmingDrawer, U.oven], sillIn: 20, toe: 'cabinet', note: 'Warming drawer under a single oven' },
  { value: 'speedOven', label: 'Speed oven', group: 'Cooking', mount: 'tall', widths: [24, 30], units: [U.speedOven], sillIn: 42, toe: 'cabinet', note: 'Convection + microwave, 17-7/8" tall' },
  { value: 'steamOven', label: 'Combi-steam oven', group: 'Cooking', mount: 'tall', widths: [24, 30], units: [U.steamOven], sillIn: 42, toe: 'cabinet', note: '17-7/8" tall; needs water or a reservoir' },
  { value: 'microwaveTrim', label: 'Built-in microwave', group: 'Cooking', mount: 'tall', widths: [27, 30], units: [U.microwave], sillIn: 54, toe: 'cabinet', note: 'With its trim kit, in a tall cabinet' },
  { value: 'vacuumDrawer', label: 'Vacuum seal drawer', group: 'Cooking', mount: 'tall', widths: [24, 30], units: [U.vacuumDrawer], sillIn: 36, toe: 'cabinet', note: '5-1/2" tall; leave a 2" filler beside it so the lid opens' },
  { value: 'microwaveDrawer', label: 'Microwave drawer', group: 'Cooking', mount: 'baseTop', widths: [24, 30], units: [U.microwaveDrawer], toe: 'cabinet', note: 'Under the counter at the top of a base box' },
  { value: 'warmingDrawer', label: 'Warming drawer', group: 'Cooking', mount: 'baseTop', widths: [30], units: [U.warmingDrawer], toe: 'cabinet', note: 'Under the counter at the top of a base box' },
  // UNDER-COUNTER OVENS (2026-10-15, Armando: "a base cabinet with the option to add a wall
  // oven"): a single oven, speed oven or steam oven built into a BASE box under the counter —
  // the classic spot under a cooktop. The opening hangs from the counter; a drawer, or a flat
  // filler when the space left is too short for one, closes the box below it.
  { value: 'ovenUnder', label: 'Wall oven, under the counter', group: 'Cooking', mount: 'baseTop', widths: [24, 27, 30], units: [U.oven], toe: 'cabinet', note: 'Single oven in a base box, 28-1/2" tall, hung from the counter; a filler below. Pair it with a cooktop above' },
  { value: 'speedOvenUnder', label: 'Speed oven, under the counter', group: 'Cooking', mount: 'baseTop', widths: [24, 30], units: [U.speedOven], toe: 'cabinet', note: 'Convection + microwave in a base box, a drawer below it' },
  { value: 'steamOvenUnder', label: 'Steam oven, under the counter', group: 'Cooking', mount: 'baseTop', widths: [24, 30], units: [U.steamOven], toe: 'cabinet', note: 'Combi-steam oven in a base box, a drawer below; needs water or a reservoir' },
  // ---- Cleaning ----
  { value: 'dishwasher', label: 'Dishwasher', group: 'Cleaning', mount: 'undercounter', widths: [18, 24], units: [U.panelDoor(34.5)], toe: 'cabinet', note: 'Fully integrated, panel-ready; its kick lines up with the toe kick' },
  { value: 'dishDrawers', label: 'Dishwasher drawers', group: 'Cleaning', mount: 'undercounter', widths: [24], units: [U.panelDrawers(34.5)], toe: 'cabinet', note: 'Double drawers, two panels' },
]

export function applianceEntry(value: unknown): ApplianceEntry | null {
  return CASEWORK_APPLIANCES.find((e) => e.value === value) ?? null
}

/** a unit's catalog record by key (for the 3D look) */
export function applianceUnitOf(key: unknown): ApplianceUnit | null {
  for (const e of CASEWORK_APPLIANCES) for (const u of e.units) if (u.key === key) return u
  return null
}

/** the height a tall box needs to hold this entry (feet): a column to 84", a stack to its top */
export function applianceNeedsFt(e: ApplianceEntry): number {
  if (e.mount === 'column') return 84 / 12
  if (e.mount === 'tall') return ((e.sillIn ?? 0) + e.units.reduce((t, u) => t + u.hIn, 0)) / 12
  return 0
}

export interface ApplianceZones {
  /** where each unit sits (feet, from the box's base) */
  openings: { z0: number; z1: number; unit: ApplianceUnit }[]
  /** the cabinet zones around them, each laid out with fronts of its own ('panel' = a flat
   *  filler panel, too short for a door) */
  fronts: { z0: number; z1: number; cfg: string; drawerCount?: number }[]
  /** the box's toe kick doesn't run in front */
  noToe: boolean
}

/**
 * Where the openings and the cabinet zones fall in a box holding `e` (feet from the box's base):
 * `czb` = the top of its toe kick, `zTop` = the box's top.
 */
export function applianceZones(e: ApplianceEntry, czb: number, zTop: number): ApplianceZones {
  const noToe = e.toe === 'none'
  const out: ApplianceZones = { openings: [], fronts: [], noToe }
  const gap = 0.25 / 12
  const drawerCfg = (h: number) => ({ cfg: 'drawers', drawerCount: h < 1 ? 1 : h < 1.75 ? 2 : 3 })
  if (e.mount === 'undercounter') {
    out.openings.push({ z0: noToe ? 0 : czb, z1: zTop, unit: e.units[0] })
  } else if (e.mount === 'baseTop') {
    const h = e.units[0].hIn / 12
    out.openings.push({ z0: zTop - h, z1: zTop, unit: e.units[0] })
    const below = zTop - h - gap - czb
    if (below > 0.4) out.fronts.push({ z0: czb, z1: zTop - h - gap, ...drawerCfg(below) })
    else if (below > gap * 2) out.fronts.push({ z0: czb, z1: zTop - h - gap, cfg: 'panel' })   // too short for a drawer: a flat filler
  } else if (e.mount === 'column') {
    const z1 = Math.min(zTop, 84 / 12)
    out.openings.push({ z0: noToe ? 0 : czb, z1, unit: e.units[0] })
    if (zTop - z1 > 0.75) out.fronts.push({ z0: z1 + gap, z1: zTop, cfg: 'doors' })
    else if (zTop - z1 > gap * 2) out.fronts.push({ z0: z1 + gap, z1: zTop, cfg: 'panel' })   // a flat filler panel
  } else {
    const sill = (e.sillIn ?? 30) / 12
    let z = sill
    for (const u of e.units) { const h = u.hIn / 12; out.openings.push({ z0: z, z1: z + h, unit: u }); z += h }
    if (sill - czb > 0.4) out.fronts.push({ z0: czb, z1: sill - gap, ...drawerCfg(sill - czb) })
    if (zTop - z > 0.75) out.fronts.push({ z0: z + gap, z1: zTop, cfg: 'doors' })
    else if (zTop - z > gap * 2) out.fronts.push({ z0: z + gap, z1: zTop, cfg: 'panel' })
  }
  return out
}

/** the casework toe kick's height (feet) — bim-geometry's CW_TOE_H */
export const CASEWORK_TOE_FT = 1 / 3

/**
 * Where a base / tall box's carcass (its fronts) starts above the box's base, by its base style —
 * the same rule as bim-geometry's casework prototype (`baseH`), so an appliance the kernel seats
 * in an opening lands exactly where the cabinet builds it.
 */
export function boxCarcassBottomFt(tp: Record<string, unknown>): number {
  const base = String(tp.baseStyle || (tp.toeKick === false ? 'flush' : 'toe'))
  if (base === 'toe' || base === 'legs') return CASEWORK_TOE_FT
  if (base === 'furniture' || base === 'feet') return Math.max(2, Math.min(8, Number(tp.baseHeightIn) || 4)) / 12
  if (base === 'floating') return Math.max(2, Math.min(30, Number(tp.floatHeightIn) || 10)) / 12
  return 0
}
